import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Stack,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { East as EastIcon, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import { isMultiWaveGroup } from "./utils/schemaTypes";
import {
  LEVEL_CONSTITUENCY,
  LEVEL_WARD,
  isTopLevel,
  levelLabel,
  levelNoun,
  levelsForSchema,
} from "./utils/geoLevels";
import ProviderLogo, { PROVIDER_LOGO_SLUGS } from "./ProviderLogo";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  glass: {
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
    borderRadius: "12px",
  },
  text: "#1a1d23",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  accent: "#0f9fab",
  border: "rgba(0,0,0,0.07)",
  surface: "#f8f9fc",
  amber: {
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.3)",
    text: "#92400e",
    pill: "rgba(245,158,11,0.14)",
  },
};

const PARTY_ORDER = [
  "pol:vi:green:cat",
  "pol:vi:labour:cat",
  "pol:vi:libdem:cat",
  "pol:vi:snp:cat",
  "pol:vi:plaid:cat",
  "pol:vi:other:cat",
  "pol:vi:conservative:cat",
  "pol:vi:reform:cat",
];

// ─── Parliament chart helpers ─────────────────────────────────────────────────
function ringArea(ring) {
  let area = 0;
  for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
    area += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return Math.abs(area / 2);
}

const calculateSeats = (rows, keys) => {
  const counts = Object.fromEntries(keys.map((k) => [k, 0]));
  rows.forEach((r) => {
    let maxKey = null, maxVal = -Infinity;
    keys.forEach((k) => {
      const v = r[k];
      if (v != null && Number.isFinite(v) && v > maxVal) { maxVal = v; maxKey = k; }
    });
    if (maxKey) counts[maxKey] += 1;
  });
  if ("pol:vi:other:cat" in counts) counts["pol:vi:other:cat"] += 18;
  return counts;
};

const ParliamentPlot = ({ seatCounts, colors, partyOrder, rows = 12, dotRadius = 0.5, width = 200, height = 100 }) => {
  if (!seatCounts || !colors) return null;
  const orderedKeys = (partyOrder || Object.keys(seatCounts)).filter((k) => (seatCounts[k] || 0) > 0);
  const seatsSeq = [];
  orderedKeys.forEach((k) => { for (let i = 0; i < seatCounts[k]; i++) seatsSeq.push(k); });
  const totalSeats = 650;
  if (!totalSeats) return null;
  const cx = width / 2, cy = height - 8;
  const pitch = dotRadius * 2.3;
  const outerR = Math.min(width / 2 - 10, 130);
  const radii = Array.from({ length: rows }, (_, i) => outerR - i * pitch * 1.1);
  const capacities = radii.map((r) => Math.floor(Math.PI * r / pitch));
  const totalCapacity = capacities.reduce((a, b) => a + b, 0);
  const scaling = 650 / totalCapacity;
  const scaledCapacities = capacities.map((c) => Math.ceil(c * scaling));
  const slots = [];
  radii.forEach((r, row) => {
    const n = scaledCapacities[row];
    for (let j = 0; j < n; j++) {
      const t = (j + 0.5) / n;
      const angle = Math.PI * t;
      slots.push({ angle, row, x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) });
    }
  });
  slots.sort((a, b) => (a.angle - b.angle) || (a.row - b.row));
  const useSlots = slots.slice(0, totalSeats);
  const filled = useSlots.map((s, i) => ({ ...s, color: colors[seatsSeq[i % seatsSeq.length]] || "#999" }));
  const majoritySlot = filled[325];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block", margin: "0 auto" }}>
      {filled.map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={dotRadius} fill={s.color} />)}
      {majoritySlot && (
        <line
          x1={cx + (radii[0] + 3) * Math.cos(majoritySlot.angle)}
          y1={cy - (radii[0] + 3) * Math.sin(majoritySlot.angle)}
          x2={cx + (radii[0] + 3) * Math.cos(majoritySlot.angle)}
          y2={cy - (radii[0] + 3) * Math.sin(majoritySlot.angle) - 10}
          stroke="#333" strokeWidth="1.5"
        />
      )}
    </svg>
  );
};

const SeatTable = ({ seatCounts, labels, colors }) => {
  if (!seatCounts) return null;
  let entries = Object.entries(seatCounts).filter(([, n]) => n > 0);
  const otherEntry = entries.find(([k]) => k.toLowerCase().includes("other"));
  entries = entries.filter(([k]) => !k.toLowerCase().includes("other")).sort((a, b) => b[1] - a[1]);
  if (otherEntry) entries.push(otherEntry);
  return (
    <table style={{ fontSize: 11, borderCollapse: "collapse", margin: "0 auto", width: "100%" }}>
      <tbody>
        {entries.map(([k, n]) => (
          <tr key={k}>
            <td style={{ padding: "2px 6px", whiteSpace: "nowrap" }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 5, backgroundColor: colors?.[k] || "#999" }} />
              <span style={{ color: T.textMuted }}>{labels?.[k] || k}</span>
            </td>
            <td style={{ padding: "2px 6px", textAlign: "right", fontWeight: 700, color: T.text, whiteSpace: "nowrap" }}>{n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ─── Shared pill button ───────────────────────────────────────────────────────
const PillButton = ({ active, onClick, children, color }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1, py: 0.4,
      borderRadius: "6px",
      border: "1px solid",
      borderColor: active ? (color || T.accent) : T.border,
      bgcolor: active ? (color ? `${color}18` : "rgba(15,159,171,0.08)") : "transparent",
      color: active ? (color || T.accent) : T.textMuted,
      fontSize: "11px", fontWeight: active ? 700 : 500,
      fontFamily: "'DM Sans', system-ui, sans-serif",
      cursor: "pointer",
      userSelect: "none",
      transition: "all 0.12s ease",
      "&:hover": { borderColor: color || T.accent, color: color || T.accent, bgcolor: color ? `${color}10` : "rgba(15,159,171,0.05)" },
    }}
  >
    {children}
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const MapToolbar = ({
  geoLevel, onGeoLevelChange,
  selectedConstituencyName, selectedAreaLabel,
  onClearSelection, zoomIn, zoomOut, resetView, goBackOneLevel,
  poliInfo, hoverArea, selectedFeature, poliInfoToplevel,
  selectedDemographics, PossibleAreas, handleselectedFeatureChange,
  allowedLevels, metricRows, group, selectedConstituencyCode,
  callAllMetrics, loadGeo, onResetDemographics,
  waveOptions, waveSelect, handlewaveSelectChange,
  showArrows, onShowArrowsToggle,
  compareWaves, onCompareWavesChange,
  compareKey, onCompareKeyChange,
  active_group,
  actionMode,
  geoLoading,
  comparableKeys,
}) => {
  const [compareExpanded, setCompareExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Reset expanded state when leaving compare mode
  React.useEffect(() => {
    if (!compareWaves) setCompareExpanded(false);
  }, [compareWaves]);

  // Close search when an area is selected
  React.useEffect(() => {
    if (selectedAreaLabel) setSearchOpen(false);
  }, [selectedAreaLabel]);

  const seatCounts = React.useMemo(() => {
    const filtered = isMultiWaveGroup(group)
      ? metricRows?.filter((g) => Number(g.Wave) === Number(waveSelect))
      : metricRows;
    if (group?.label !== "Vote intention" || !filtered?.length) return null;
    return calculateSeats(filtered, group.keys);
  }, [metricRows, waveSelect, group?.schema, group?.label, group?.keys]);

  // ── Derived values for compare controls ───────────────────────────────────
  const isTracking = isMultiWaveGroup(active_group);
  const hasWaves = waveOptions?.length > 0 && isTracking;
  const sortedWaves = [...(waveOptions || [])].sort((a, b) => a - b);
  const prevWave = sortedWaves[sortedWaves.length - 2];
  const latestWave = sortedWaves[sortedWaves.length - 1];
  const canCompare = waveOptions?.length >= 2;

  const showParliament = !!(group && metricRows && group?.label === "Vote intention" && geoLevel === "constituency");

  // Which levels this question's schema actually has. Ward stays gated on a
  // selected constituency because it only ever shows one constituency's wards;
  // local authority is national, so it needs no selection first.
  const levelOptions = levelsForSchema(group?.schema).map((val) => ({
    val,
    label: levelLabel(val),
    disabled: val === LEVEL_WARD && !selectedConstituencyName,
  }));

  const isLikertGrid = active_group?.type === "likert_grid";
  const isTargetMode = actionMode === "filter";
  const isVoteIntention = String(active_group?.label || "").toLowerCase().includes("vote intention");
  const isDontKnow = (key) => {
    const kk = String(key || "").toLowerCase();
    const lbl = String((isLikertGrid ? active_group?.subLabels?.[key] : active_group?.labels?.[key]) || "").toLowerCase();
    return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
  };
  // In compare mode, only offer keys with previous-wave data. comparableKeys
  // holds response-key COLUMNS ("ind:trust_gambling:tend_trust:cat"), but a
  // likert grid's displayKeys are sub-variable IDS ("trust_gambling") — match
  // a sub-variable if any of its response keys is comparable.
  const isComparableKey = (k) => {
    if (!compareWaves || !comparableKeys) return true;
    if (!isLikertGrid) return comparableKeys.has(k);
    for (const ck of comparableKeys) {
      if (String(ck).split(":")[1] === String(k)) return true;
    }
    return false;
  };
  const displayKeys = (isLikertGrid
    ? (active_group?.subVariables || [])
    : (active_group?.keys || [])
  ).filter((k) => !(isVoteIntention && isDontKnow(k)))
   .filter(isComparableKey);
  const getLabel = (key) => isLikertGrid ? (active_group?.subLabels?.[key] || key) : (active_group?.labels?.[key] || key);
  const getColor = (key) => isLikertGrid ? active_group?.subColors?.[key] : active_group?.colors?.[key];

  const compareMode = compareKey === "__top__" ? "top_pos"
    : compareKey === "__top_neg__" ? "top_neg"
    : "selected";
  const selectedPartyKey = displayKeys.includes(compareKey)
    ? compareKey
    : (displayKeys[0] || null);

  // ── Demographic pills ──────────────────────────────────────────────────────
  const demoPills = [];
  if (selectedDemographics) {
    Object.entries(selectedDemographics).forEach(([key, val]) => {
      const lbls = Array.isArray(val) ? val : val ? [val] : [];
      lbls.forEach((l) => {
        if (key === "segment") {
          // Segment labels arrive joined ("Octopus Energy, Ovo Energy"); split
          // so energy providers get one branded pill each with their logo.
          String(l).split(", ").filter(Boolean).forEach((name) => {
            demoPills.push(PROVIDER_LOGO_SLUGS[name]
              ? { key: `segment-${name}`, label: name, logo: name }
              : { key: `segment-${name}`, label: `Segment: ${name}` });
          });
        } else {
          demoPills.push({ key: `${key}-${l}`, label: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}` });
        }
      });
    });
  }
  const hasFilters = demoPills.length > 0;

  return (
    <>
      {/* ── Top-left: collapsed trigger / expanded search ──────────────────── */}
      <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
        {!searchOpen ? (
          /* Collapsed: two compact icon-pill buttons */
          <Box sx={{ display: "flex", gap: 0.75 }}>
            {/* Geo level pill */}
            <Box id="tour-geo-level" sx={{
              ...T.glass,
              display: "flex", alignItems: "center",
              px: 0.5, py: 0.5, gap: 0.25,
            }}>
              {levelOptions.map(({ val, label, disabled }, i, arr) => (
                <React.Fragment key={val}>
                  <Box
                    onClick={() => { if (!disabled) onGeoLevelChange(val); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.25, py: 0.55,
                      borderRadius: "7px",
                      bgcolor: geoLevel === val ? T.accent : "transparent",
                      color: geoLevel === val ? "#fff" : disabled ? T.textFaint : T.textMuted,
                      fontSize: "12px", fontWeight: 600,
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.45 : 1,
                      transition: "all 0.12s",
                      userSelect: "none",
                      "&:hover": !disabled && geoLevel !== val ? { bgcolor: "rgba(0,0,0,0.05)", color: T.text } : {},
                    }}
                  >
                    {label}
                    {geoLoading?.[val] && (
                      <CircularProgress
                        size={9}
                        thickness={5}
                        sx={{ color: geoLevel === val ? "rgba(255,255,255,0.8)" : T.accent, flexShrink: 0 }}
                      />
                    )}
                  </Box>
                  {i < arr.length - 1 && <Box sx={{ width: "1px", height: 18, bgcolor: T.border, flexShrink: 0 }} />}
                </React.Fragment>
              ))}
            </Box>

            {/* Search trigger */}
            <Tooltip title="Find area" placement="right">
              <Box
                id="tour-area-search"
                onClick={() => setSearchOpen(true)}
                sx={{
                  ...T.glass,
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.25, py: 0.65,
                  cursor: "pointer",
                  transition: "all 0.12s",
                  "&:hover": { boxShadow: `0 2px 12px rgba(0,0,0,0.12), 0 0 0 2px rgba(15,159,171,0.15)` },
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: T.textMuted }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                  {`Find ${levelNoun(geoLevel)}…`}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        ) : (
          /* Expanded: full search bar */
          <Box sx={{
            ...T.glass,
            display: "flex", alignItems: "center", gap: 1,
            px: 1.25, py: 0.75,
            width: 420,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 0 2px rgba(15,159,171,0.18)",
          }}>
            <SearchIcon sx={{ fontSize: 17, color: T.accent, flexShrink: 0 }} />
            <Autocomplete
              open
              disablePortal
              autoFocus
              options={PossibleAreas || []}
              getOptionLabel={(o) => o.name || ""}
              value={selectedFeature}
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", fontSize: "13px",
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  "& fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": { borderColor: "transparent" },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder={`Find ${levelNoun(geoLevel)}…`}
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { height: 34, bgcolor: "transparent" },
                    "& .MuiOutlinedInput-input": { padding: "4px 4px", fontSize: "13px" },
                  }}
                />
              )}
              onChange={async (event, newValue) => {
                if (!newValue) { resetView({ LevelToResetTo: LEVEL_CONSTITUENCY }); setSearchOpen(false); return; }
                const areaCode = newValue.area_cd;
                const areaName = newValue.name;
                setSearchOpen(false);
                // Picking a top-level area (constituency or local authority)
                // drills into the wards it contains, exactly as clicking it on
                // the map does.
                if (isTopLevel(geoLevel)) {
                  await handleselectedFeatureChange({ level: geoLevel, value: areaCode, name: areaName });
                  onGeoLevelChange(LEVEL_WARD);
                  try {
                    await callAllMetrics(geoLevel, areaCode);
                    await loadGeo(LEVEL_WARD, areaCode, { doFit: true });
                    await callAllMetrics(LEVEL_WARD, areaCode);
                  } catch (err) { console.error(err); }
                  return;
                }
                const isSameWard = selectedFeature?.level === LEVEL_WARD && selectedFeature?.value === areaCode;
                if (isSameWard && selectedConstituencyName) { goBackOneLevel ? goBackOneLevel() : resetView({ LevelToResetTo: LEVEL_CONSTITUENCY }); return; }
                await handleselectedFeatureChange({ level: LEVEL_WARD, value: areaCode, name: areaName });
                await callAllMetrics(LEVEL_WARD, selectedConstituencyCode);
              }}
              onBlur={() => setSearchOpen(false)}
            />
            <Tooltip title="Close" placement="right">
              <IconButton size="small" onClick={() => setSearchOpen(false)} sx={{ color: T.textFaint, flexShrink: 0, "&:hover": { color: T.text } }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* ── Top-right: selected area + demographics ─────────────────────────── */}
      {(selectedAreaLabel || hasFilters) && (
        <Box sx={{
          position: "absolute", top: 16, right: 16, zIndex: 10,
          ...T.glass,
          minWidth: 220, maxWidth: 300,
          overflow: "hidden",
          ...(hasFilters ? { border: `1px solid ${T.amber.border}`, boxShadow: `0 2px 12px rgba(0,0,0,0.08), 0 0 0 3px rgba(245,158,11,0.1)` } : {}),
        }}>
          {/* Area row */}
          {selectedAreaLabel && (
            <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.2 }}>
                  Selected area
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.text, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                  {selectedAreaLabel}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                {goBackOneLevel && (
                  <Tooltip title="Go up one level" placement="bottom">
                    <IconButton size="small" onClick={goBackOneLevel} sx={{ width: 28, height: 28, borderRadius: "7px", border: `1px solid ${T.border}`, bgcolor: T.surface, color: T.textMuted, "&:hover": { bgcolor: "rgba(0,0,0,0.06)", color: T.text } }}>
                      <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Clear selection" placement="bottom">
                  <IconButton size="small" onClick={resetView} sx={{ width: 28, height: 28, borderRadius: "7px", border: `1px solid ${T.border}`, bgcolor: T.surface, color: T.textMuted, "&:hover": { bgcolor: "rgba(220,38,38,0.06)", borderColor: "rgba(220,38,38,0.3)", color: "#dc2626" } }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Demographics row */}
          {hasFilters && (
            <>
              {selectedAreaLabel && <Box sx={{ height: "1px", bgcolor: T.amber.border, mx: 1.5 }} />}
              <Box sx={{ px: 1.5, py: 0.85, bgcolor: T.amber.bg }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 11, color: T.amber.text }} />
                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.amber.text, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Filtered
                    </Typography>
                  </Box>
                  <Tooltip title="Clear all filters" placement="bottom">
                    <IconButton size="small" onClick={onResetDemographics} sx={{ width: 20, height: 20, color: T.amber.text, opacity: 0.7, "&:hover": { opacity: 1 } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                  {demoPills.map(({ key, label, logo }) => (
                    <Box key={key} sx={{
                      fontSize: "10px", fontWeight: 600, color: T.amber.text,
                      bgcolor: T.amber.pill, border: `1px solid ${T.amber.border}`,
                      borderRadius: logo ? "999px" : "5px", px: 0.75, py: 0.2, lineHeight: 1.6,
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      "@keyframes pillIn": {
                        from: { opacity: 0, transform: "translateY(-3px) scale(0.96)" },
                        to: { opacity: 1, transform: "translateY(0) scale(1)" },
                      },
                      animation: "pillIn 0.25s ease",
                    }}>
                      {logo && <ProviderLogo name={logo} size={15} />}
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── Left column: zoom (top) + parliament (bottom) ───────────────────── */}
      {/* Single flex column so the two panels never overlap regardless of size */}
      <Box sx={{
        position: "absolute", left: 16, top: 64, bottom: 16, zIndex: 10,
        display: "flex", flexDirection: "column", alignItems: "flex-start",
        pointerEvents: "none",
      }}>
        {/* Zoom controls — pushed to vertical centre of remaining space */}
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", pointerEvents: "none" }}>
          <Box sx={{
            ...T.glass,
            display: "flex", flexDirection: "column",
            p: 0.5, gap: 0,
            overflow: "hidden",
            pointerEvents: "auto",
          }}>
            {[
              { icon: <ZoomInIcon sx={{ fontSize: 18 }} />, onClick: zoomIn, title: "Zoom in" },
              { icon: <ZoomOutIcon sx={{ fontSize: 18 }} />, onClick: zoomOut, title: "Zoom out" },
              { icon: <RestartAltIcon sx={{ fontSize: 18 }} />, onClick: resetView, title: "Reset view" },
            ].map(({ icon, onClick, title }, i, arr) => (
              <React.Fragment key={title}>
                <Tooltip title={title} placement="right">
                  <IconButton onClick={onClick} size="small" sx={{
                    width: 34, height: 34, borderRadius: "7px",
                    color: T.textMuted,
                    "&:hover": { bgcolor: "rgba(15,159,171,0.07)", color: T.accent },
                    transition: "all 0.12s",
                  }}>
                    {icon}
                  </IconButton>
                </Tooltip>
                {i < arr.length - 1 && <Box sx={{ height: "1px", width: "100%", bgcolor: T.border }} />}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Parliament plot — sits at bottom of left column, pushing zoom up */}
        {showParliament && (
          <Box sx={{
            ...T.glass,
            px: 1.75, pt: 1.25, pb: 1.25,
            minWidth: 160,
            pointerEvents: "auto",
          }}>
            <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75, textAlign: "center" }}>
              Projected seats
            </Typography>
            <ParliamentPlot
              seatCounts={seatCounts}
              colors={group.colors}
              rows={15}
              dotRadius={1.9}
              partyOrder={PARTY_ORDER}
            />
            <Box sx={{ mt: 0.75 }}>
              <SeatTable seatCounts={seatCounts} labels={group.labels} colors={group.colors} />
            </Box>
          </Box>
        )}
      </Box>

      {hasWaves && (
        <Box id="tour-wave-select" sx={{
          position: "absolute", bottom: 10, right: 16, zIndex: 10,
          ...T.glass,
          width: 248,
          // Long option lists (e.g. sector importance's 16 categories) used to
          // grow the panel past the top of the viewport — cap and scroll instead.
          maxHeight: "calc(100vh - 90px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}>
          {!compareWaves ? (
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25 }}>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                Wave
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.25 }}>
                {sortedWaves.map((wave) => (
                  <Box
                    key={wave}
                    onClick={() => handlewaveSelectChange(wave)}
                    sx={{
                      px: 1.25, py: 0.45,
                      borderRadius: "7px",
                      border: "1px solid",
                      borderColor: waveSelect === wave ? T.accent : T.border,
                      bgcolor: waveSelect === wave ? "rgba(15,159,171,0.08)" : T.surface,
                      color: waveSelect === wave ? T.accent : T.textMuted,
                      fontSize: "12px", fontWeight: waveSelect === wave ? 700 : 500,
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      cursor: "pointer",
                      transition: "all 0.12s",
                      "&:hover": { borderColor: T.accent, color: T.accent },
                    }}
                  >
                    {wave}
                  </Box>
                ))}
              </Box>
              {canCompare && (
                <Box
                  onClick={() => onCompareWavesChange?.(true)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                    px: 1, py: 0.7,
                    borderRadius: "8px",
                    border: `1px solid ${T.border}`,
                    bgcolor: T.surface,
                    cursor: "pointer",
                    transition: "all 0.12s",
                    "&:hover": { borderColor: T.accent, bgcolor: "rgba(15,159,171,0.05)" },
                  }}
                >
                  <EastIcon sx={{ fontSize: 13, color: T.textFaint }} />
                  <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    Compare waves
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Compare mode header */
            <>
              <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "rgba(15,159,171,0.04)", borderBottom: `1px solid ${T.border}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                      Wave {prevWave}
                    </Typography>
                    <EastIcon sx={{ fontSize: 12, color: T.accent }} />
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                      Wave {latestWave}
                    </Typography>
                  </Box>
                  <Tooltip title={showArrows !== false ? "Hide movement arrows" : "Show movement arrows"} placement="top">
                    <Box
                      onClick={() => onShowArrowsToggle?.()}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: 26, height: 26, borderRadius: "6px",
                        border: "1px solid",
                        borderColor: showArrows !== false ? T.accent : T.border,
                        bgcolor: showArrows !== false ? "rgba(15,159,171,0.08)" : "transparent",
                        color: showArrows !== false ? T.accent : T.textFaint,
                        cursor: "pointer",
                        transition: "all 0.12s",
                        "&:hover": { borderColor: T.accent, color: T.accent },
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: 12, transform: "rotate(-45deg)" }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Tooltip title="Exit compare" placement="top">
                  <IconButton size="small" onClick={() => onCompareWavesChange?.(false)} sx={{ width: 24, height: 24, color: T.textFaint, "&:hover": { color: "#dc2626", bgcolor: "rgba(220,38,38,0.06)" } }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Compare controls */}
              {active_group?.keys?.length > 1 && (
                <Box sx={{ px: 1.5, py: 1.25 }}>

                  {!isTargetMode && (
                    <>
                      {/* Mode selector */}
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                        Map mode
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 1.25 }}>
                        {[
                          { val: "top_pos", label: "Biggest gainer" },
                          { val: "top_neg", label: "Biggest loser" },
                          { val: "selected", label: isLikertGrid ? "By sector" : "By category" },
                        ].map(({ val, label }) => (
                          <Box
                            key={val}
                            onClick={() => {
                              if (val === "top_pos") onCompareKeyChange?.("__top__");
                              else if (val === "top_neg") onCompareKeyChange?.("__top_neg__");
                              else onCompareKeyChange?.(selectedPartyKey || "__top__");
                            }}
                            sx={{
                              flex: 1, textAlign: "center",
                              px: 0.5, py: 0.55,
                              borderRadius: "7px",
                              border: "1px solid",
                              borderColor: compareMode === val ? T.accent : T.border,
                              bgcolor: compareMode === val ? "rgba(15,159,171,0.08)" : T.surface,
                              color: compareMode === val ? T.accent : T.textMuted,
                              fontSize: "10.5px", fontWeight: compareMode === val ? 700 : 500,
                              fontFamily: "'DM Sans', system-ui, sans-serif",
                              cursor: "pointer",
                              lineHeight: 1.3,
                              transition: "all 0.12s",
                              "&:hover": { borderColor: T.accent, color: T.accent },
                            }}
                          >
                            {label}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {/* Category picker — only in "selected" mode */}
                  {!isTargetMode && compareMode === "selected" && (
                    <>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.65 }}>
                        {isLikertGrid ? "Sector" : (active_group?.label || "Category")}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45, mb: 1.25 }}>
                        {displayKeys.map((key) => {
                          const col = getColor(key);
                          const lbl = getLabel(key);
                          const isActive = (compareKey === key) || (!displayKeys.includes(compareKey) && key === selectedPartyKey);
                          return (
                            <Box
                              key={key}
                              onClick={() => onCompareKeyChange?.(key)}
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: 0.5,
                                px: 0.85, py: 0.35,
                                borderRadius: "6px",
                                border: "1px solid",
                                borderColor: isActive ? (col || T.accent) : T.border,
                                bgcolor: isActive ? (col ? `${col}18` : "rgba(15,159,171,0.07)") : "transparent",
                                color: isActive ? (col || T.accent) : T.textMuted,
                                fontSize: "10.5px", fontWeight: isActive ? 700 : 500,
                                fontFamily: "'DM Sans', system-ui, sans-serif",
                                cursor: "pointer",
                                transition: "all 0.12s",
                                "&:hover": { borderColor: col || T.accent, color: col || T.accent },
                              }}
                            >
                              {col && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />}
                              {lbl}
                            </Box>
                          );
                        })}
                        {!isLikertGrid && active_group?.polarity && (
                          <Box
                            onClick={() => onCompareKeyChange?.("net")}
                            sx={{
                              display: "inline-flex", alignItems: "center",
                              px: 0.85, py: 0.35,
                              borderRadius: "6px",
                              border: "1px solid",
                              borderColor: compareKey === "net" ? T.accent : T.border,
                              bgcolor: compareKey === "net" ? "rgba(15,159,171,0.07)" : "transparent",
                              color: compareKey === "net" ? T.accent : T.textMuted,
                              fontSize: "10.5px", fontWeight: compareKey === "net" ? 700 : 500,
                              fontFamily: "'DM Sans', system-ui, sans-serif",
                              cursor: "pointer",
                              transition: "all 0.12s",
                              "&:hover": { borderColor: T.accent, color: T.accent },
                            }}
                          >
                            Net
                          </Box>
                        )}
                      </Box>
                    </>
                  )}

                  {/* Map key */}
                  <Box sx={{ pt: 1, borderTop: `1px solid ${T.border}` }}>
                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.6 }}>
                      Map key
                    </Typography>
                    {isTargetMode || compareMode === "selected" ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        {[
                          { color: "#15803d", label: "Gaining support" },
                          { color: "#e5e7eb", label: "Stable / no change" },
                          { color: "#b91c1c", label: "Losing support" },
                        ].map(({ color, label }) => (
                          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "10.5px", color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ mt: 0.4, display: "flex", gap: 1.5 }}>
                          {[{ color: "#16a34a", label: "↑ Gaining" }, { color: "#dc2626", label: "↓ Losing" }].map(({ color, label }) => (
                            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: "9.5px", color: T.textFaint }}>{label}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        {displayKeys.map((key) => {
                          const col = getColor(key);
                          if (!col) return null;
                          return (
                            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: col, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: "10.5px", color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                                {getLabel(key)}
                              </Typography>
                            </Box>
                          );
                        })}
                        <Typography sx={{ mt: 0.25, fontSize: "9.5px", color: T.textFaint, fontStyle: "italic" }}>
                          Shade = movement strength
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

    </>
  );
};

export default MapToolbar;
